import { Headphones, MapPin, Phone, ShieldCheck, type LucideIcon } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { Link } from 'react-router';

import { copyrightStyle } from '@/features/storefront/appearance';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { LanguageSwitcherSlot } from '@/features/storefront/i18n/LanguageSwitcher';
import { AppStoreBadges } from '@/features/storefront/layouts/chrome/FooterExtras';
import { InstallAppSlot } from '@/features/storefront/pwa/InstallAppSlot';
import { useContent, useShopContent } from '@/features/storefront/content/useContent';
import { useCategoryPath, useShelfCategories } from '@/features/storefront/hooks/useShelfCategories';
import { BlogLinks, MenuLink, PageLinks } from '@/features/storefront/layouts/chrome/ChromeParts';
import {
  FOOTER_CATEGORY_LIMIT,
  categoryColumns,
  menuItems,
  type FooterProps,
} from '@/features/storefront/layouts/chrome/types';
import { cn } from '@/lib/cn';

/**
 * The footer grid, by how many link columns stand beside the wordmark.
 *
 * Written out for the same reason Serra's, Voltic's and Tools & Machine's are:
 * Tailwind reads these class names out of the source and never sees a string
 * built at runtime.
 */
const FOOTER_GRID: Record<number, string> = {
  0: '',
  1: 'lg:grid-cols-[1.5fr_1fr]',
  2: 'lg:grid-cols-[1.5fr_1fr_1fr]',
  3: 'lg:grid-cols-[1.5fr_1fr_1fr_1fr]',
  4: 'lg:grid-cols-[1.5fr_1fr_1fr_1fr_1fr]',
};

/**
 * The service strip above the footer proper.
 *
 * Four standing facts about the shop rather than four links: where it is, when
 * it answers, what it promises. A superstore's footer opens with these because
 * a sofa is a decision somebody wants to talk to a person about, and burying
 * the phone number under a column heading is how a shop loses that
 * conversation.
 *
 * The words were the theme's for as long as there was nowhere to put a
 * merchant's - which is why they were written to be true of the platform
 * rather than of any particular shop: opening hours nobody claimed, an address
 * nobody invented. There is somewhere now (`hyper/content.ts`), so these four
 * are the theme's *defaults* and a merchant who answers the phone on Sundays
 * can say so. Only the icons stay the theme's, because an icon is drawing.
 */
const SERVICES: { icon: LucideIcon; key: string }[] = [
  { icon: Headphones, key: 'support' },
  { icon: Phone, key: 'phone' },
  { icon: ShieldCheck, key: 'secure' },
  { icon: MapPin, key: 'showroom' },
];

function ServiceStrip() {
  const text = useContent('hyper');

  return (
    <div style={{ backgroundColor: 'var(--storefront-surface)' }}>
      <StorefrontContainer className="grid gap-8 py-9 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map(({ icon: Icon, key }) => (
          <div key={key} className="flex items-start gap-3.5">
            <span
              className="grid size-11 shrink-0 place-items-center rounded-full"
              style={{
                backgroundColor: 'var(--storefront-background)',
                color: 'var(--storefront-primary)',
              }}
            >
              <Icon className="size-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-bold">{text(`footer.service.${key}.title`)}</p>
              <p className="mt-1 text-[13px]" style={{ color: 'var(--storefront-muted)' }}>
                {text(`footer.service.${key}.body`)}
              </p>
            </div>
          </div>
        ))}
      </StorefrontContainer>
    </div>
  );
}

/**
 * One column: a heading in capitals over its links.
 *
 * Solid ink rather than the accent, unlike the two themes that invert their
 * footers. This one stays on the page's own white, so the weight of the
 * heading is enough to separate a column's name from its contents and the red
 * can go on staying the sale colour and nothing else.
 */
function Column({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div>
      <p
        className="mb-4 text-[11px] font-bold uppercase tracking-[0.14em]"
        aria-hidden={title ? undefined : true}
      >
        {title ?? ' '}
      </p>
      {children}
    </div>
  );
}

/**
 * The superstore's back page: a service strip, a directory, and a fine-print
 * bar in the brand cobalt.
 *
 * The one footer in the set that signs off in colour rather than in ink. The
 * shop opens on a cobalt rail and closes on a cobalt bar, and everything in
 * between is white with grey tiles on it - which is the whole of this theme in
 * one sentence.
 *
 * The column arithmetic is Serra's, Voltic's and Tools & Machine's, because
 * the problem is the same one: the merchant's footer menu, the shop's own
 * shelves, its pages and its blogs, each skipped when empty and the grid sized
 * to what is left - the alternative being a directory of headings with nothing
 * under them.
 */
export const Footer: FC<FooterProps> = ({ storeName, footer, description, menu, preview }) => {
  const items = menuItems(menu);
  const shelfColumns = categoryColumns(
    useShelfCategories({ limit: FOOTER_CATEGORY_LIMIT, enabled: !preview }),
  );
  const categoryPath = useCategoryPath();
  const text = useContent('hyper');
  // The words every theme's footer shares - the fine print's sign-off and a
  // column's continuation - are the shop's, not Hyper's.
  const copy = useShopContent();
  const columns = (items.length > 0 ? 1 : 0) + shelfColumns.length + (preview ? 0 : 1);
  // The merchant may well have called their own menu "Shop" already, and two
  // columns under one heading read as a single list broken in half. Compared
  // against this shop's *own* word for the column, so a merchant who renamed
  // it keeps the rule rather than losing it to their own edit.
  const shopTitle = text('footer.column.shop');
  const shelvesTitle =
    menu?.name.trim().toLowerCase() === shopTitle.trim().toLowerCase()
      ? text('footer.column.departments')
      : shopTitle;

  return (
    <footer className="text-[13px]" style={{ backgroundColor: 'var(--storefront-background)' }}>
      <ServiceStrip />

      <StorefrontContainer
        className={cn('grid gap-10 pb-14 pt-14 text-start sm:grid-cols-2', FOOTER_GRID[columns])}
      >
        <div>
          <p
            className="text-2xl uppercase tracking-[-0.01em]"
            style={{ fontFamily: 'var(--storefront-heading-font)', fontWeight: 800 }}
          >
            {storeName}
          </p>
          {description ? (
            <p
              className="mt-4 max-w-[24rem] leading-relaxed"
              style={{ color: 'var(--storefront-muted)' }}
            >
              {description}
            </p>
          ) : null}
        </div>

        {items.length > 0 ? (
          <Column title={menu?.name ?? text('footer.column.company')}>
            <nav aria-label={menu?.name} className="flex flex-col items-start gap-3">
              {items.map((item) => (
                <MenuLink
                  key={`${item.label}:${item.url}`}
                  item={item}
                  preview={preview}
                  className="hover:opacity-60"
                />
              ))}
            </nav>
          </Column>
        ) : null}

        {shelfColumns.map((column, index) => (
          <Column key={column[0].id} title={index === 0 ? shelvesTitle : undefined}>
            <nav
              aria-label={
                index === 0 ? shelvesTitle : copy('theme.footer_continued', { title: shelvesTitle })
              }
              className="flex flex-col items-start gap-3"
            >
              {column.map((category) => (
                <Link
                  key={category.id}
                  to={categoryPath(category.slug)}
                  className="hover:opacity-60"
                >
                  {category.name}
                </Link>
              ))}
            </nav>
          </Column>
        ))}

        {preview ? null : (
          <Column title={text('footer.column.help')}>
            <PageLinks className="mb-3 flex-col items-start justify-start gap-3" />
            <BlogLinks className="flex-col items-start justify-start gap-3" />
            <InstallAppSlot className="mt-4" />
            <AppStoreBadges footer={footer} preview={preview} className="mt-4" />
          </Column>
        )}
      </StorefrontContainer>

      {/* The cobalt bar. The shop opened on this colour four decks up. */}
      <div
        style={{
          backgroundColor: 'var(--storefront-primary)',
          color: 'var(--storefront-background)',
        }}
      >
        <StorefrontContainer className="flex flex-wrap justify-between gap-x-6 gap-y-2 py-5 text-[11px] uppercase tracking-[0.1em]">
          <LanguageSwitcherSlot placement="footer" preview={preview} className="normal-case tracking-normal" />
          <span>{text('footer.fine_print.prices')}</span>
          <span style={copyrightStyle(footer)}>
            &copy; {new Date().getFullYear()} {storeName} &middot;{' '}
            {copy('theme.powered_by', { brand: 'NeonCarts' })}
          </span>
        </StorefrontContainer>
      </div>
    </footer>
  );
};
